import { ClipboardEvent } from "react";
import { ColumnsContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { TextInput } from "@shared/designSystem/TextInput";
import { SmallText } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";

// Two numbers separated by comma, semicolon or spaces, as copied from Google Maps
// ("-34.6037, -58.3816").
const coordinatesPairRegex = /(-?\d{1,3}(?:[.]\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:[.]\d+)?)/;

export const sanitizeCoordinateInput = (value: string): string => value.replace(/[^0-9.\-]/g, "");

/** Returns an error message, or "" when the latitude is valid (-90 to 90). */
export const validateLatitude = (value: string): string => {
    if (!value.trim()) {
        return "%s es obligatoria".localized("Latitud".localized());
    }
    const number = Number(value);
    if (!Number.isFinite(number) || number < -90 || number > 90) {
        return "La latitud debe estar entre -90 y 90".localized();
    }
    return "";
};

/** Returns an error message, or "" when the longitude is valid (-180 to 180). */
export const validateLongitude = (value: string): string => {
    if (!value.trim()) {
        return "%s es obligatoria".localized("Longitud".localized());
    }
    const number = Number(value);
    if (!Number.isFinite(number) || number < -180 || number > 180) {
        return "La longitud debe estar entre -180 y 180".localized();
    }
    return "";
};

interface CoordinatesFieldsProps {
    latitude: string;
    longitude: string;
    latitudeError?: string;
    longitudeError?: string;
    /** True after a "lat, lng" paste was split automatically */
    wasSplit: boolean;
    disabled?: boolean;
    onLatitudeChange: (value: string) => void;
    onLongitudeChange: (value: string) => void;
    /** Called when a "lat, lng" pair is pasted in either input */
    onPairPasted: (latitude: string, longitude: string) => void;
    onEnterPress?: () => void;
}

/**
 * Latitude and longitude inputs. Pasting "latitud, longitud" (as copied from
 * Google Maps) in either of them fills both. Used by the qontact point
 * Información tab and the "Nuevo Qontact Point" modal.
 */
export const CoordinatesFields: React.FC<CoordinatesFieldsProps> = ({
    latitude,
    longitude,
    latitudeError,
    longitudeError,
    wasSplit,
    disabled = false,
    onLatitudeChange,
    onLongitudeChange,
    onPairPasted,
    onEnterPress,
}) => {
    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
        const text = event.clipboardData.getData("text");
        const match = coordinatesPairRegex.exec(text);
        if (match) {
            event.preventDefault();
            onPairPasted(match[1], match[2]);
        }
    };

    return (
        <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
            <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                <div onPaste={handlePaste}>
                    <TextInput
                        label={"Latitud *".localized()}
                        value={latitude}
                        placeholder="-34.6037"
                        inputMode="decimal"
                        error={latitudeError}
                        disabled={disabled}
                        onChange={(value) => onLatitudeChange(sanitizeCoordinateInput(value))}
                        onEnterPress={onEnterPress}
                    />
                </div>
                <div onPaste={handlePaste}>
                    <TextInput
                        label={"Longitud *".localized()}
                        value={longitude}
                        placeholder="-58.3816"
                        inputMode="decimal"
                        error={longitudeError}
                        disabled={disabled}
                        onChange={(value) => onLongitudeChange(sanitizeCoordinateInput(value))}
                        onEnterPress={onEnterPress}
                    />
                </div>
            </ColumnsContainer>
            {wasSplit && (
                <SmallText
                    text={"Separamos latitud y longitud automáticamente.".localized()}
                    color={designSystemStyles.colorSuccess}
                />
            )}
        </VerticalContainer>
    );
};

export default CoordinatesFields;
